// PixelSurface → GPU テクスチャのキャッシュ。CPU タイルが真実、GPU はキャッシュ。
// 表面の revision が変わったら再アップロードする (Phase 2 で部分更新・チャンク化する)。

import type { Rect } from '@/types/geometry'
import type { PixelSurface } from '@/pixels/pixelSurface'
import { createTexture, deleteTexture, type Texture } from '@/gl/texture'
import { TILE_SIZE, parseTileKey } from '@/pixels/tile'
import { rectEquals } from '@/types/geometry'

interface Entry {
  texture: Texture
  revision: number
  /** 表面座標での矩形 (surface.bounds()) */
  rect: Rect
  lastUsed: number
}

export class LayerTextureCache {
  private entries = new Map<string, Entry>()
  private tick = 0
  /** VRAM 予算 (バイト)。超えたら使われていない順に捨てる (CPU タイルから再アップロードできる) */
  budgetBytes: number
  /** 計測用: 直近の render での全アップロード / 部分アップロードの回数 */
  stats = { fullUploads: 0, partialUploads: 0, tilesUploaded: 0 }

  constructor(private gl: WebGL2RenderingContext, private maxTextureSize: number, budgetBytes = 512 * 1024 * 1024) {
    this.budgetBytes = budgetBytes
  }

  /** 表面のテクスチャと表面座標矩形を返す。空の表面なら null */
  get(surface: PixelSurface): { texture: Texture; rect: Rect } | null {
    const bounds = surface.bounds()
    if (!bounds) {
      this.evict(surface.id)
      return null
    }
    const cur = this.entries.get(surface.id)
    if (cur && cur.revision === surface.revision) {
      cur.lastUsed = ++this.tick
      return { texture: cur.texture, rect: cur.rect }
    }
    if (bounds.w > this.maxTextureSize || bounds.h > this.maxTextureSize) {
      throw new Error(`レイヤーが GPU のテクスチャ上限 (${this.maxTextureSize}px) を超えています`)
    }
    // 境界が同じで変更タイルが分かるなら部分更新
    if (cur && rectEquals(cur.rect, bounds)) {
      const changed = surface.changesSince(cur.revision)
      const tilesInTex = (bounds.w / TILE_SIZE) * (bounds.h / TILE_SIZE)
      if (changed && changed.size <= Math.max(4, tilesInTex * 0.25)) {
        this.uploadTiles(surface, cur, changed)
        cur.revision = surface.revision
        cur.lastUsed = ++this.tick
        this.stats.partialUploads++
        this.stats.tilesUploaded += changed.size
        return { texture: cur.texture, rect: cur.rect }
      }
    }
    const data = surface.readRect(bounds)
    this.evict(surface.id)
    const texture = createTexture(this.gl, bounds.w, bounds.h, surface.channels, data)
    this.entries.set(surface.id, { texture, revision: surface.revision, rect: bounds, lastUsed: ++this.tick })
    this.stats.fullUploads++
    return { texture, rect: bounds }
  }

  private uploadTiles(surface: PixelSurface, e: Entry, keys: Set<string>) {
    const gl = this.gl
    const c = surface.channels
    gl.bindTexture(gl.TEXTURE_2D, e.texture.tex)
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1)
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false)
    const zeros = new Uint8ClampedArray(TILE_SIZE * TILE_SIZE * c)
    for (const key of keys) {
      const { tx, ty } = parseTileKey(key)
      const x = tx * TILE_SIZE - e.rect.x
      const y = ty * TILE_SIZE - e.rect.y
      if (x < 0 || y < 0 || x + TILE_SIZE > e.rect.w || y + TILE_SIZE > e.rect.h) continue
      const tile = surface.getTile(key)
      const data = tile ? tile.data : zeros
      gl.texSubImage2D(gl.TEXTURE_2D, 0, x, y, TILE_SIZE, TILE_SIZE, c === 4 ? gl.RGBA : gl.RED, gl.UNSIGNED_BYTE, data)
    }
    gl.bindTexture(gl.TEXTURE_2D, null)
  }

  /** 予算を超えていたら、used に含まれない古いものから捨てる */
  trimToBudget(used: Set<string>): number {
    let bytes = this.byteLength()
    if (bytes <= this.budgetBytes) return 0
    const candidates = [...this.entries.entries()].filter(([id]) => !used.has(id)).sort((a, b) => a[1].lastUsed - b[1].lastUsed)
    let evicted = 0
    for (const [id, e] of candidates) {
      if (bytes <= this.budgetBytes) break
      bytes -= e.rect.w * e.rect.h * e.texture.channels
      this.evict(id)
      evicted++
    }
    return evicted
  }

  resetStats(): void {
    this.stats = { fullUploads: 0, partialUploads: 0, tilesUploaded: 0 }
  }

  evict(id: string): void {
    const e = this.entries.get(id)
    if (!e) return
    deleteTexture(this.gl, e.texture)
    this.entries.delete(id)
  }

  /** 使われなくなった表面のテクスチャを解放する */
  retainOnly(ids: Set<string>): void {
    for (const id of [...this.entries.keys()]) if (!ids.has(id)) this.evict(id)
  }

  clear(): void {
    for (const id of [...this.entries.keys()]) this.evict(id)
  }

  byteLength(): number {
    let n = 0
    for (const e of this.entries.values()) n += e.rect.w * e.rect.h * e.texture.channels
    return n
  }
}
